/**
 * MEMCORE Modul B (Labor) — Portal-Client für die einzige Schreibstraße.
 *
 * Architektur (SKALIERUNG_FIRMENZENTRALE_UND_BRAIN.md §7.3/§7.8):
 *   Schreiben NUR über den n8n-Curator-Webhook (aktion: proposal/vorschau/
 *   supersede/tombstone) — das Portal schreibt nie direkt in mem_vermerke.
 *   Lesen: PocketBase mem_vermerke mit dem Kunden-Token; die PB-Rule
 *   `user_id = @request.auth.id` (create-memcore-collections-labor.ps1,
 *   Modul-B-Schritt) erzwingt 1 Sitz = 1 Gedächtnis serverseitig.
 *
 * Feature-Gate: constants.ts `isMemcoreEnabled()` — VITE_MEMCORE_CURATOR_URL
 * leer = komplettes Gedächtnis-UI aus (Labor-only, Premium später Kunden-GEX).
 */
import type { Message } from '../InlineChat';

// ── Typen ────────────────────────────────────────────────────────────────────

export interface MemcoreContext {
  /** Kanzlei-Kontext (§7.2) — Portal: customer.kanzlei */
  kanzleiId: string;
  /** 1 Sitz = 1 Gedächtnis — Portal: PB customers Record-ID (= @request.auth.id) */
  userId: string;
}

export interface MemVermerk {
  /** PB-Record-ID (für UI-Keys; Schreiben läuft über vermerk_id + Curator) */
  id: string;
  vermerk_id: string;
  inhalt: string;
  quelle_typ: string;
  quelle_ref: string;
  vertrauen: string;
  gueltig_von: string;
  gueltig_bis: string;
  ersetzt: string;
  created?: string;
}

export interface CuratorResult {
  status: 'ok' | 'vorschau' | 'duplikat' | 'abgelehnt' | 'fehler' | 'netzwerk';
  aktion?: string;
  merkpunkte?: string;
  vermerk_id?: string;
  inhalt?: string;
  grund?: string;
  detail?: string;
  muster?: string[];
  dedupe?: string;
  /** Modul C: deterministischer Query-Hash (nie Klartext im Register). */
  query_hash?: string;
  treffer?: number;
  vermerke?: MemRecallHit[];
}

/** Ein Treffer aus memcoreRecall (Modul C — on-demand Lesen). */
export interface MemRecallHit {
  vermerk_id: string;
  inhalt: string;
  gueltig_von: string;
  vertrauen: string;
  quelle_typ?: string;
  score?: number;
}

/** Nachweis-Pfeil unter ARKO-Antworten (§7.5 — ausklappbar, nicht im Fließtext). */
export type MemProofItem = Pick<MemRecallHit, 'vermerk_id' | 'inhalt' | 'gueltig_von' | 'vertrauen'>;

// ── Curator-Webhook ──────────────────────────────────────────────────────────

const CURATOR_TIMEOUT_MS = 120000; // Ollama-Destillat kann dauern (Labor)

async function curatorRequest(
  curatorUrl: string,
  body: Record<string, unknown>,
  timeoutMs: number = CURATOR_TIMEOUT_MS,
): Promise<CuratorResult> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(curatorUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) {
      return {
        status: 'fehler',
        grund: `http_${res.status}`,
        detail:
          res.status === 404
            ? 'Der Gedächtnis-Workflow (MEMCORE_CURATOR) ist auf dem Server nicht aktiv.'
            : `Server-Fehler ${res.status}. Bitte später erneut versuchen.`,
      };
    }
    const data = (await res.json()) as Record<string, unknown>;
    if (typeof data?.status === 'string') return data as unknown as CuratorResult;
    return { status: 'fehler', grund: 'antwort_unlesbar', detail: 'Unerwartete Server-Antwort.' };
  } catch (err: unknown) {
    const isTimeout = (err as { name?: string })?.name === 'AbortError';
    return {
      status: 'netzwerk',
      detail: isTimeout
        ? 'Das Gedächtnis denkt noch nach (Modell lädt). Bitte erneut versuchen.'
        : 'Gedächtnis-Dienst nicht erreichbar. Verbindung prüfen.',
    };
  }
}

/** Schritt 1 „Session beenden“ (Variante A §7.7): Merkpunkte erzeugen, nichts schreiben. */
export function memcoreVorschau(
  curatorUrl: string,
  ctx: MemcoreContext,
  transcript: string,
  anweisung?: string,
): Promise<CuratorResult> {
  const roh = anweisung?.trim()
    ? `Anweisung des Anwalts, was zu speichern ist: ${anweisung.trim()}\n\n${transcript}`
    : transcript;
  return curatorRequest(curatorUrl, {
    aktion: 'vorschau',
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    quelle_typ: 'mensch',
    schreibpfad: 'session_komprimierung',
    chat_komprimiert: roh.slice(0, 7500),
  });
}

/** Schritt 2 „Session beenden“: bestätigte Merkpunkte exakt so speichern (direkt). */
export function memcoreSessionSpeichern(
  curatorUrl: string,
  ctx: MemcoreContext,
  merkpunkte: string,
  threadId: string,
  transcript: string,
): Promise<CuratorResult> {
  return curatorRequest(curatorUrl, {
    aktion: 'proposal',
    direkt: true,
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    quelle_typ: 'mensch',
    quelle_ref: `portal:session:${threadId}`,
    schreibpfad: 'session_freigabe',
    vorschlag_inhalt: merkpunkte.slice(0, 1900),
    chat_komprimiert: transcript.slice(0, 7500),
  });
}

/** Daumen hoch (2-Schritt §7.7): Nachricht + optionaler Kommentar → Destillat → belegt. */
export function memcoreDaumenSpeichern(
  curatorUrl: string,
  ctx: MemcoreContext,
  nachricht: string,
  kommentar: string,
  quelleRef: string,
): Promise<CuratorResult> {
  const roh = kommentar.trim()
    ? `Kommentar des Anwalts: ${kommentar.trim()}\n\nFreigegebene ARKO-Antwort:\n${nachricht}`
    : `Freigegebene ARKO-Antwort:\n${nachricht}`;
  return curatorRequest(curatorUrl, {
    aktion: 'proposal',
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    quelle_typ: 'modell',
    quelle_ref: quelleRef,
    schreibpfad: 'daumen_freigabe',
    chat_komprimiert: roh.slice(0, 7500),
  });
}

/** Onboarding-Interview (§7.7 Cold-Start): eine Antwort = ein Proposal. */
export function memcoreOnboardingSpeichern(
  curatorUrl: string,
  ctx: MemcoreContext,
  frage: string,
  antwort: string,
  index: number,
): Promise<CuratorResult> {
  return curatorRequest(curatorUrl, {
    aktion: 'proposal',
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    quelle_typ: 'mensch',
    quelle_ref: `portal:onboarding:${index + 1}`,
    schreibpfad: 'onboarding_interview',
    chat_komprimiert: `Onboarding-Frage: ${frage}\nAntwort des Anwalts: ${antwort}`.slice(0, 7500),
  });
}

// ── Skills (Mein Gedächtnis — manuell, keine Akten) ─────────────────────────

/** Kategorie-Chips: optional; Chip-Skills landen 100 % im ARKO-memContext. */
export const SKILL_CHIPS = [
  { id: 'ton', label: 'Ton' },
  { id: 'verbote', label: 'Verbote' },
  { id: 'kanzleistil', label: 'Kanzleistil' },
] as const;

export type SkillChipId = (typeof SKILL_CHIPS)[number]['id'];

/** quelle_ref-Prefix für Chip-Skills (kein neues PB-Schema). */
export const SKILL_CHIP_QUELLE_PREFIX = 'portal:skill:chip:';
/** Freitext-Skill ohne Chip — nur Recall/unbewusst, nicht auto-memContext. */
export const SKILL_FREETEXT_QUELLE_REF = 'portal:skill:freetext';

export function isSkillChipVermerk(v: Pick<MemVermerk, 'quelle_ref'> | { quelle_ref?: string }): boolean {
  return String(v.quelle_ref ?? '').startsWith(SKILL_CHIP_QUELLE_PREFIX);
}

export function isSkillVermerk(v: Pick<MemVermerk, 'quelle_ref'> | { quelle_ref?: string }): boolean {
  const ref = String(v.quelle_ref ?? '');
  return ref.startsWith('portal:skill:');
}

export function skillChipIdFromQuelleRef(quelleRef: string): SkillChipId | null {
  if (!quelleRef.startsWith(SKILL_CHIP_QUELLE_PREFIX)) return null;
  const id = quelleRef.slice(SKILL_CHIP_QUELLE_PREFIX.length);
  return SKILL_CHIPS.some((c) => c.id === id) ? (id as SkillChipId) : null;
}

export function formatSkillInhalt(text: string, chipId?: SkillChipId | null): string {
  const body = text.trim().slice(0, 1900);
  if (!body) return '';
  if (!chipId) return body;
  const chip = SKILL_CHIPS.find((c) => c.id === chipId);
  if (!chip) return body;
  const prefix = `[Skill · ${chip.label}] `;
  return `${prefix}${body}`.slice(0, 1900);
}

/** Manueller Skill (Freitext ± Chip) — schreibpfad gedaechtnis_manuell → vertrauen belegt. */
export function memcoreSkillSpeichern(
  curatorUrl: string,
  ctx: MemcoreContext,
  inhalt: string,
  chipId?: SkillChipId | null,
): Promise<CuratorResult> {
  const formatted = formatSkillInhalt(inhalt, chipId);
  const quelle_ref = chipId
    ? `${SKILL_CHIP_QUELLE_PREFIX}${chipId}`
    : SKILL_FREETEXT_QUELLE_REF;
  return curatorRequest(curatorUrl, {
    aktion: 'proposal',
    direkt: true,
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    quelle_typ: 'mensch',
    quelle_ref,
    schreibpfad: 'gedaechtnis_manuell',
    vorschlag_inhalt: formatted,
    chat_komprimiert: formatted.slice(0, 7500),
  });
}

/** Chip-Skill → memContext-Eintrag (explizit bei jedem ARKO-Send). */
export function skillVermerkToMemItem(v: MemVermerk): MemProofItem {
  return {
    vermerk_id: v.vermerk_id,
    inhalt: v.inhalt,
    gueltig_von: v.gueltig_von ? v.gueltig_von.slice(0, 10) : '',
    vertrauen: v.vertrauen || 'belegt',
  };
}

/** Bearbeiten = neue Version (ersetzt-Kette §7.6) — intern Fortschreiben via Curator. */
export function memcoreSupersede(
  curatorUrl: string,
  ctx: MemcoreContext,
  vermerkId: string,
  neuerInhalt: string,
): Promise<CuratorResult> {
  return curatorRequest(curatorUrl, {
    aktion: 'supersede',
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    vermerk_id: vermerkId,
    vorschlag_inhalt: neuerInhalt.slice(0, 1900),
    quelle_ref: 'portal:gedaechtnis:bearbeiten',
  });
}

/** Löschen = Tombstone (§7.6) — für den Anwalt „weg“, im Register belegt. */
export function memcoreTombstone(
  curatorUrl: string,
  ctx: MemcoreContext,
  vermerkId: string,
): Promise<CuratorResult> {
  return curatorRequest(curatorUrl, {
    aktion: 'tombstone',
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    vermerk_id: vermerkId,
  });
}

// ── Modul C: deterministischer Recall (§7.5/§7.8) ───────────────────────────

/** Chip-Prefix im Eingabefeld — Recall nur bei Chip-Klick oder Whitelist. */
export const MEM_RECALL_CHIP_PREFIX = 'Aus meinem Gedächtnis:\n\n';

/**
 * Whitelist-Phrasen (case-insensitive DE) — ohne Chip löst nur diese Recall aus.
 * Kein LLM-Entscheid „passende Frage“ (Hermes-Lehre, §7.5).
 *
 * Stream 4 (ARKO V2 — MEMCORE im Chat): Fall-orientierte Phrasen ergänzt, damit
 * „Was weißt du über den Fall?“ deterministisch Recall auslöst (Done #2). Beide
 * Schreibweisen von „weißt“/„weisst“ abgedeckt; Match ist `String.includes` auf
 * dem kleingeschriebenen Originaltext.
 */
export const MEM_RECALL_WHITELIST: readonly string[] = [
  'wie letztes mal',
  'erinnerst du dich',
  'aus meinem gedächtnis',
  'was weißt du über',
  'was weisst du über',
  'fall-eckdaten',
  'falleckdaten',
  'stand der dinge',
  'fasse den fall',
];

/** NFC-normalisieren wie lexcore/audit.py (query_hash-Vertrag). */
export function normalizeMemcoreQuery(query: string): string {
  return query.normalize('NFC').toLowerCase().trim().replace(/\s+/g, ' ');
}

/** Recall auslösen? Chip aktiv ODER eine Whitelist-Phrase im Text. */
export function shouldMemcoreRecall(text: string, chipActive: boolean): boolean {
  if (chipActive) return true;
  const lower = text.toLowerCase();
  return MEM_RECALL_WHITELIST.some((phrase) => lower.includes(phrase));
}

/** Such-Query für Qdrant: Chip-Prefix und Whitelist-Floskeln entfernen. */
export function stripMemRecallNoise(text: string): string {
  let t = text.trim();
  const prefixHead = MEM_RECALL_CHIP_PREFIX.split('\n')[0].toLowerCase();
  if (t.toLowerCase().startsWith(prefixHead)) {
    t = t.slice(t.indexOf(':') + 1).trim();
  }
  for (const phrase of MEM_RECALL_WHITELIST) {
    const re = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    t = t.replace(re, ' ').replace(/\s+/g, ' ').trim();
  }
  return t.replace(/^[,:\s-–]+/, '').trim() || text.trim();
}

/**
 * On-demand Recall über Curator (Qdrant memcore + PB mem_vermerke).
 * Schreibt pro Treffer ein read-Event ins Register (query_hash, nie Klartext).
 */
export function memcoreRecall(
  curatorUrl: string,
  ctx: MemcoreContext,
  recallQuery: string,
  trigger: 'chip' | 'whitelist',
): Promise<CuratorResult> {
  const q = stripMemRecallNoise(recallQuery);
  return curatorRequest(curatorUrl, {
    aktion: 'recall',
    kanzlei_id: ctx.kanzleiId,
    user_id: ctx.userId,
    recall_query: q.slice(0, 1900),
    schreibpfad: trigger === 'chip' ? 'arko_recall_chip' : 'arko_recall_whitelist',
    quelle_ref: `portal:arko:recall:${trigger}`,
  });
}

// ── Stream 4: Fall-Eckdaten im Thread (Auto-Vorbefüllung) ───────────────────
//
// WAS: Der Anwalt „merkt“ Fall-Eckdaten (Mandant, Akte, Gegner) aus EINER
// Chat-Nachricht. Diese gepinnten Fakten hängen anschließend bei JEDER weiteren
// Nachricht dieses Threads automatisch als memContext am Webhook-Body — so
// werden spätere Entwürfe vorbefüllt, ohne dass ARKO erneut nachfragt (Done #1).
//
// DSGVO / PII_GATE (Stream 7): Gepinnte Fakten leben ausschließlich CLIENT-SEITIG
// im Thread (localStorage, derselbe Ort wie der Chatverlauf, per Sitz/Kunde
// getrennt; Logout löscht Auth, nicht den Chat — Wipe nur per UI-Löschen /
// clearArkoChatStorage). Sie werden NICHT roh nach PocketBase geschrieben
// (der Curator-PII-Guard lehnt Mandanten-PII bewusst ab).
// Auf dem Weg zum LLM pseudonymisiert der bestehende PII_GATE den memContext-Text
// pro Anfrage (PERSON_1 …) und das Quality-Gate setzt die Tokens nur in der
// finalen Antwort lokal zurück. Cross-Thread/PB-Gedächtnis bleibt der Curator
// (pseudonymsichere Präferenzen) + on-demand Recall.

/** Ein vom Anwalt gepinnter Fall-Eckdaten-Fakt (Thread-lokal). */
export interface PinnedFact {
  id: string;
  inhalt: string;
  /** Epoch-ms — Anlegezeitpunkt (für gueltig_von + Sortierung). */
  ts: number;
}

/** Max. gepinnte Fakten pro Thread — hält memContext/Prompt kompakt. */
export const MAX_PINNED_FACTS = 12;

/** Max. Länge eines gepinnten Fakts (deckt sich mit Curator vorschlag_inhalt). */
export const MAX_PINNED_FACT_LEN = 1900;

function pinnedFactId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `fall_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `fall_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Text → PinnedFact (getrimmt, gekappt). Leerer Text ⇒ null (nichts pinnen). */
export function makePinnedFact(text: string, now: number = Date.now()): PinnedFact | null {
  const inhalt = (text ?? '').trim().slice(0, MAX_PINNED_FACT_LEN);
  if (!inhalt) return null;
  return { id: pinnedFactId(), inhalt, ts: now };
}

/**
 * Fakt zur Liste hinzufügen: Duplikate (gleicher normalisierter Text) werden
 * übersprungen, Liste auf MAX_PINNED_FACTS begrenzt (älteste fallen raus).
 */
export function addPinnedFact(list: PinnedFact[], fact: PinnedFact | null): PinnedFact[] {
  if (!fact) return list;
  const norm = (s: string) => s.normalize('NFC').toLowerCase().trim().replace(/\s+/g, ' ');
  const key = norm(fact.inhalt);
  if (list.some((f) => norm(f.inhalt) === key)) return list;
  const next = [...list, fact];
  return next.length > MAX_PINNED_FACTS ? next.slice(next.length - MAX_PINNED_FACTS) : next;
}

/** Einen Fakt entfernen (UI „×“). */
export function removePinnedFact(list: PinnedFact[], id: string): PinnedFact[] {
  return list.filter((f) => f.id !== id);
}

/** PinnedFact → memContext-Eintrag (Webhook-Body / Nachweis-Pfeil). */
export function pinnedFactToMemItem(fact: PinnedFact): MemProofItem {
  return {
    vermerk_id: fact.id,
    inhalt: fact.inhalt,
    gueltig_von: new Date(fact.ts).toISOString().slice(0, 10),
    vertrauen: 'anwalt_bestaetigt',
  };
}

/**
 * memContext für den Webhook-Body bauen: Chip-Skills + gepinnte Fall-Eckdaten
 * (Auto-Vorbefüllung) + optionale on-demand Recall-Treffer, dedupliziert nach
 * vermerk_id. Prioritätsliste (Skills/Pins) wird nie abgeschnitten; Recall nur
 * bis MAX_PINNED_FACTS Gesamteinträge, sofern noch Platz.
 */
export function mergeMemContext(
  pinned: MemProofItem[],
  recall?: MemProofItem[] | null,
): MemProofItem[] {
  const out: MemProofItem[] = [];
  const seen = new Set<string>();
  for (const item of pinned) {
    if (!item || !item.inhalt) continue;
    const key = item.vermerk_id || item.inhalt;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  const priorityLen = out.length;
  for (const item of recall ?? []) {
    if (!item || !item.inhalt) continue;
    const key = item.vermerk_id || item.inhalt;
    if (seen.has(key)) continue;
    if (out.length >= Math.max(MAX_PINNED_FACTS, priorityLen)) break;
    seen.add(key);
    out.push(item);
  }
  return out;
}

// ── PocketBase-Lesepfad („Mein Gedächtnis“) ─────────────────────────────────

function pbFilterLiteral(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Aktive Vermerke des eigenen Sitzes. Die PB-Rule scopt serverseitig auf
 * @request.auth.id — der Filter hier ist Defense-in-Depth + Tombstone-Ausschluss.
 */
export async function listMeineVermerke(
  pbBaseUrl: string,
  authToken: string,
  userId: string,
): Promise<MemVermerk[]> {
  const base = pbBaseUrl.replace(/\/$/, '');
  // PB Labor: sort=-created → 400 auf mem_vermerke; leerer Text braucht "" nicht '' (§7.2 gueltig_bis).
  const filter = encodeURIComponent(
    `user_id = '${pbFilterLiteral(userId)}' && gueltig_bis ?= ""`,
  );
  const res = await fetch(
    `${base}/api/collections/mem_vermerke/records?perPage=200&sort=-gueltig_von&filter=${filter}`,
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? 'Gedächtnis-Collection nicht gefunden (mem_vermerke fehlt auf diesem PB).'
        : res.status === 403
          ? 'Kein Lesezugriff auf mem_vermerke — Portal-Rule fehlt (listRule/viewRule: user_id = @request.auth.id). Admin: create-memcore-collections-labor.ps1 ausführen.'
          : `Gedächtnis konnte nicht geladen werden (HTTP ${res.status}).`,
    );
  }
  const data = (await res.json()) as { items?: Array<Record<string, unknown>> };
  return (data.items ?? []).map((r) => ({
    id: String(r.id ?? ''),
    vermerk_id: String(r.vermerk_id ?? ''),
    inhalt: String(r.inhalt ?? ''),
    quelle_typ: String(r.quelle_typ ?? ''),
    quelle_ref: String(r.quelle_ref ?? ''),
    vertrauen: String(r.vertrauen ?? ''),
    gueltig_von: String(r.gueltig_von ?? ''),
    gueltig_bis: String(r.gueltig_bis ?? ''),
    ersetzt: String(r.ersetzt ?? ''),
    created: typeof r.created === 'string' ? r.created : undefined,
  }));
}

/**
 * Aktive Chip-Skills des Sitzes (PB-Lesen) — für expliziten ARKO-memContext.
 * Freitext-Skills ohne Chip sind bewusst nicht enthalten.
 */
export async function listSkillChipMemContext(
  pbBaseUrl: string,
  authToken: string,
  userId: string,
): Promise<MemProofItem[]> {
  const items = await listMeineVermerke(pbBaseUrl, authToken, userId);
  return items.filter(isSkillChipVermerk).map(skillVermerkToMemItem);
}

// ── Chat-Transkript für die Komprimierung ───────────────────────────────────

/** Verlauf → Klartext für den Curator. Erste Bot-Begrüßung wird übersprungen. */
export function buildChatTranscript(messages: Message[], maxChars = 7000): string {
  const lines: string[] = [];
  messages.forEach((m, i) => {
    if (i === 0 && m.role === 'bot') return; // Begrüßung trägt nichts
    lines.push(`${m.role === 'user' ? 'Anwalt' : 'ARKO'}: ${m.text}`);
  });
  const full = lines.join('\n');
  // Bei Überlänge das Ende behalten — die jüngsten Aussagen sind die relevanten
  return full.length > maxChars ? full.slice(-maxChars) : full;
}

/** Mindestens eine User-Nachricht = Session hat speicherbaren Inhalt (Nudge-Kriterium). */
export function hatSpeicherbarenInhalt(messages: Message[]): boolean {
  return messages.some((m) => m.role === 'user' && m.text.trim().length > 0);
}

// ── Onboarding (§7.7 Cold-Start) ────────────────────────────────────────────

/** ~10 Fragen: Stil, Rechtsgebiete, Präferenzen — bewusst ohne Mandantenbezug. */
export const ONBOARDING_FRAGEN: string[] = [
  'In welchen Rechtsgebieten arbeiten Sie hauptsächlich?',
  'Mit welchen Gesetzen arbeiten Sie täglich (z. B. MRG, ABGB, UGB)?',
  'Welche Arten von Schriftsätzen und Dokumenten erstellen Sie am häufigsten?',
  'Wie soll ARKO formulieren — eher klassisch-juristisch oder modern-sachlich?',
  'Bevorzugen Sie kurze Zusammenfassungen oder ausführliche Analysen?',
  'In welcher Form sollen Entwürfe gehalten sein (Anrede, Sprache, Tonfall)?',
  'Gibt es Formulierungen oder Floskeln, die ARKO vermeiden soll?',
  'Wie sollen Fristen und Termine in Antworten hervorgehoben werden?',
  'Welche wiederkehrenden Arbeitsabläufe soll ARKO kennen (z. B. Diktat → Reinschrift)?',
  'Was soll sich ARKO über Ihre Arbeitsweise sonst unbedingt merken?',
];

const ONBOARDING_FLAG_PREFIX = 'arkorbit_memcore_onboarding_v1__';

export function isMemcoreOnboardingDone(customerId: string): boolean {
  try {
    return localStorage.getItem(`${ONBOARDING_FLAG_PREFIX}${customerId}`) === 'done';
  } catch {
    return true; // ohne Storage lieber nicht nerven
  }
}

export function setMemcoreOnboardingDone(customerId: string): void {
  try {
    localStorage.setItem(`${ONBOARDING_FLAG_PREFIX}${customerId}`, 'done');
  } catch {
    /* noop */
  }
}

// ── UI-Hilfen ────────────────────────────────────────────────────────────────

export function curatorFehlertext(r: CuratorResult): string {
  if (r.status === 'abgelehnt' && r.grund?.startsWith('pii_verdacht')) {
    return (
      'Nicht gespeichert: Der Text enthält vermutlich personenbezogene Daten (' +
      (r.muster?.join(', ') || 'PII') +
      '). Bitte ohne Namen, Kontakt- oder Falldaten formulieren.'
    );
  }
  if (r.status === 'abgelehnt') return r.detail || 'Der Vorschlag wurde abgelehnt.';
  if (r.status === 'duplikat') return 'Bereits im Gedächtnis vorhanden (kein Duplikat angelegt).';
  return r.detail || 'Unerwarteter Fehler beim Gedächtnis-Dienst.';
}
